import { useState, useEffect, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { sendMessage, fetchChatHistory, ChatHistoryResponse, fetchMediaBlob } from "@/services/chat-service";
import { cn } from "@/lib/utils";
import { Send, MessageCircle, Clock, Calendar as CalendarIcon, Loader2, AlertCircle, RotateCw, Sparkles, Inbox, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatPhone } from "@/utils/formatters";
import { PatientsByPhoneDialog } from "./PatientsByPhoneDialog";

interface ChatHistoryPanelProps {
  ticketId?: string;
  cellphone?: string;
  patientName?: string;
  clinicTradeName?: string;
  clinicId?: string;
}

// Templates rápidos — atalhos para mensagens comuns da clínica.
// {nome} é substituído pelo primeiro nome do paciente (omitido se desconhecido).
// {clinica} é substituído pelo trade_name da clínica selecionada.
const QUICK_TEMPLATES: { label: string; text: string }[] = [
  { label: "Saudação",    text: "Olá{nome}! Aqui é da {clinica}. Como podemos ajudar?" },
  { label: "Cadastrar",   text: "Olá! Para realizarmos seu cadastro, preciso de algumas informações:\n\n📋 *Nome completo*\n🪪 *CPF*\n📅 *Data de nascimento* (DD/MM/AAAA)\n📧 *E-mail*\n💳 *Número da carteirinha do convênio* (se tiver)\n\nPode enviar por aqui mesmo! 😊" },
  { label: "Inatividade", text: "Olá{nome}! Como não tivemos retorno, vamos encerrar esta conversa por enquanto. Se precisar de algo, é só nos chamar aqui. Até logo! 😊" },
  { label: "Agradecer",   text: "Obrigado pelo retorno{nome}! Qualquer dúvida, estamos por aqui." },
];

type LocalMessage = {
  id: string;
  content: string;
  media_id: string;
  msg_type: string;
  direction: string;
  created_at: string;
  _pending?: boolean;
  _failed?: boolean;
};

function MessageContent({ ticketId, msg }: { ticketId: string; msg: { content: string; media_id: string; msg_type: string; direction: string } }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLoad = async () => {
    if (blobUrl || loading || !msg.media_id || !ticketId) return;
    setLoading(true);
    try {
      const url = await fetchMediaBlob(ticketId, msg.msg_type, msg.media_id);
      setBlobUrl(url);
    } catch {
      console.error("Erro ao carregar mídia");
    } finally {
      setLoading(false);
    }
  };

  if (msg.msg_type === "image") {
    return blobUrl ? (
      <img src={blobUrl} alt="imagem" className="max-w-[260px] rounded-lg cursor-pointer" />
    ) : (
      <button onClick={handleLoad} className="flex items-center gap-2 text-xs opacity-70 hover:opacity-100 transition">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "🖼 Ver imagem"}
      </button>
    );
  }

  if (msg.msg_type === "audio") {
    return blobUrl ? (
      <audio controls src={blobUrl} className="max-w-[260px]" />
    ) : (
      <button onClick={handleLoad} className="flex items-center gap-2 text-xs opacity-70 hover:opacity-100 transition">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "🎵 Ouvir áudio"}
      </button>
    );
  }

  if (msg.msg_type === "document") {
    return blobUrl ? (
      <a href={blobUrl} target="_blank" rel="noreferrer" className="text-xs underline">
        📄 Abrir documento
      </a>
    ) : (
      <button onClick={handleLoad} className="flex items-center gap-2 text-xs opacity-70 hover:opacity-100 transition">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "📄 Ver documento"}
      </button>
    );
  }

  return <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>;
}

export function ChatHistoryPanel({ ticketId, cellphone, patientName, clinicTradeName, clinicId }: ChatHistoryPanelProps) {
  const [data, setData] = useState<ChatHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [pendingMessages, setPendingMessages] = useState<LocalMessage[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showPatientsDialog, setShowPatientsDialog] = useState(false);

  const [limit, setLimit] = useState("20");
  const [days, setDays] = useState("7");

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const loadHistory = async () => {
    if (!ticketId) { setData(null); return; }
    setLoading(true);
    try {
      const response = await fetchChatHistory(ticketId, Number(limit), Number(days));
      setData(response);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Erro ao carregar chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendText = async (text: string) => {
    if (!text.trim() || !ticketId) return;

    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: LocalMessage = {
      id: tempId,
      content: text,
      media_id: "",
      msg_type: "text",
      direction: "OUT",
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setPendingMessages(prev => [...prev, optimistic]);
    setNewMessage("");
    setTimeout(scrollToBottom, 50);

    setIsSending(true);
    try {
      await sendMessage(ticketId, text);
      setPendingMessages(prev => prev.filter(m => m.id !== tempId));
      await loadHistory();
    } catch (error) {
      console.error(error);
      setPendingMessages(prev => prev.map(m => m.id === tempId ? { ...m, _pending: false, _failed: true } : m));
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSending) return;
    sendText(newMessage);
  };

  const handleRetry = (msg: LocalMessage) => {
    setPendingMessages(prev => prev.filter(m => m.id !== msg.id));
    sendText(msg.content);
  };

  const applyTemplate = (tpl: string) => {
    const isUnknown = !patientName || patientName === "Paciente Desconhecido";
    const firstName = isUnknown ? "" : (patientName.split(/\s+/)[0]);
    const nomeToken = firstName ? ` ${firstName}` : "";
    const clinica = clinicTradeName || "Clínica";
    const text = tpl
      .replace(/\{nome\}/g, nomeToken)
      .replace(/\{clinica\}/g, clinica);
    setNewMessage(text);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    loadHistory();
  }, [ticketId, limit, days]);

  useEffect(() => {
    setPendingMessages([]);
    setShowTemplates(false);
    setNewMessage("");
  }, [ticketId]);

  // Empty state — nenhuma conversa selecionada
  if (!ticketId) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-muted/20 px-8 text-center">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
          <Inbox className="h-7 w-7 text-accent" />
        </div>
        <h2 className="font-display text-xl italic text-foreground mb-2">
          Selecione uma conversa
        </h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          Escolha uma conversa na lista ao lado para visualizar o histórico e responder.
        </p>
        <div className="mt-6 flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
          <kbd className="px-1.5 py-0.5 rounded border border-border bg-card">↑</kbd>
          <kbd className="px-1.5 py-0.5 rounded border border-border bg-card">↓</kbd>
          <span className="font-sans not-italic">navegar</span>
          <span className="mx-1">·</span>
          <kbd className="px-1.5 py-0.5 rounded border border-border bg-card">e</kbd>
          <span className="font-sans">resolver</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-display font-semibold text-base">
            {(patientName || cellphone || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 overflow-hidden">
            <h2 className="font-display text-base font-medium truncate">
              {patientName || "Paciente s/ nome"}
            </h2>
            <p className="text-xs text-muted-foreground truncate tabular-nums">
              {cellphone ? formatPhone(cellphone) : "Sem telefone"}
            </p>
          </div>

          {cellphone && clinicId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[10px] gap-1"
              onClick={() => setShowPatientsDialog(true)}
              title="Ver pacientes cadastrados nesse telefone"
            >
              <Users className="h-3 w-3" />
              Pacientes
            </Button>
          )}

          <div className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded text-[10px] font-display italic text-muted-foreground">
            <MessageCircle className="h-3 w-3" />
            {data?.total_messages || 0} msgs
          </div>

          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger className="h-7 w-[72px] text-[10px] border-border bg-muted/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20 msgs</SelectItem>
              <SelectItem value="50">50 msgs</SelectItem>
              <SelectItem value="100">100 msgs</SelectItem>
            </SelectContent>
          </Select>

          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-7 w-[72px] text-[10px] border-border bg-muted/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="15">15 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-smooth" ref={scrollRef}>
        {loading && !data ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className={cn("flex flex-col gap-1 max-w-[80%]", i % 2 === 0 ? "ml-auto items-end" : "items-start")}>
                <Skeleton className="h-10 w-48 rounded-2xl" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        ) : (
          [...(data?.history || []), ...pendingMessages].map((msg) => {
            const local = msg as LocalMessage;
            return (
              <div
                key={msg.id}
                className={cn(
                  "flex flex-col gap-1 max-w-[75%]",
                  msg.direction === "OUT" ? "ml-auto items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "px-3 py-2 rounded-2xl text-xs shadow-sm transition-opacity",
                    msg.direction === "OUT"
                      ? "bg-primary text-primary-foreground rounded-tr-sm"
                      : "bg-card text-foreground rounded-tl-sm border border-border",
                    local._pending && "opacity-60",
                    local._failed && "!border !border-destructive/60 !bg-destructive/10 !text-destructive"
                  )}
                >
                  <MessageContent ticketId={ticketId} msg={msg} />
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground px-1 font-display italic">
                  {local._pending && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                  {local._failed && (
                    <>
                      <AlertCircle className="h-2.5 w-2.5 text-destructive" />
                      <span className="text-destructive">falha ao enviar</span>
                      <button
                        onClick={() => handleRetry(local)}
                        className="ml-1 inline-flex items-center gap-0.5 text-destructive hover:underline not-italic font-sans"
                      >
                        <RotateCw className="h-2.5 w-2.5" /> tentar de novo
                      </button>
                    </>
                  )}
                  {!local._pending && !local._failed && (
                    <>
                      <Clock className="h-2.5 w-2.5" />
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      <CalendarIcon className="h-2.5 w-2.5 ml-1" />
                      {new Date(msg.created_at).toLocaleDateString([], { day: "2-digit", month: "short" })}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-card">
        {showTemplates && (
          <div className="px-3 pt-3 pb-1 flex flex-wrap gap-1.5 border-b border-border/60 bg-muted/30">
            {QUICK_TEMPLATES.map((tpl) => (
              <button
                key={tpl.label}
                type="button"
                onClick={() => applyTemplate(tpl.text)}
                className="px-2.5 py-1 rounded-full text-[11px] font-display italic bg-card border border-border hover:border-accent hover:text-accent hover:bg-accent/5 transition-all"
                title={tpl.text}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-center gap-2 p-3">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(
              "h-9 w-9 shrink-0 transition-colors",
              showTemplates && "bg-accent/10 text-accent"
            )}
            onClick={() => setShowTemplates(v => !v)}
            title="Templates rápidos"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="flex-1 h-9 text-sm border-border bg-background focus-visible:ring-primary"
            disabled={loading}
          />
          <Button
            type="submit"
            size="icon"
            className="h-9 w-9 shrink-0 shadow-sm"
            disabled={!newMessage.trim() || loading}
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>

      <PatientsByPhoneDialog
        open={showPatientsDialog}
        onOpenChange={setShowPatientsDialog}
        cellphone={cellphone}
        clinicId={clinicId}
      />
    </div>
  );
}
